<template>
  <div class="leaf-table" ref="leafTableRef" :style="leafTableStyle">

    <div class="leaf-header-row" ref="headerRowRef">
      <div
        class="leaf-header"
        :style="{ color: queueConfig.numberHeaderColor, fontSize: headerFontSize + 'px', textAlign: headerTextAlign }"
      >
        {{ numberTxt.toUpperCase() }}
      </div>
      <div
        class="leaf-header"
        :style="{ color: queueConfig.counterHeaderColor, fontSize: headerFontSize + 'px', textAlign: headerTextAlign }"
      >
        {{ counterTxt.toUpperCase() }}
      </div>
    </div>
    <div class="leaf-rows" :class="{ 'few-rows': isFewRows }">
      <div v-for="(row, idx) in queueData" :key="row.id || idx"   class="leaf-row">
        <div class="leaf-cell leaf-number-cell" :style="{ ...numberCellStyle, flex: numberFlex, fontSize: rowFontSize + 'px' }">
          {{ row.ticket }}
        </div>
        <div class="leaf-cell leaf-counter-cell" :style="{ ...counterCellStyle, flex: counterFlex, fontSize: rowFontSize + 'px' }">
          {{ row.counter }}
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
export default {
  name: "QueueLeafWidget",
};
</script>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick, defineProps, watch } from "vue";
const props = defineProps<{
  queueData: Array<any>;
  queueConfig: any;
  borderRadius: number;
  config: any;
  numberTxt: string;
  counterTxt: string;
}>();

const leafTableRef = ref<HTMLElement | null>(null);
const headerRowRef = ref<HTMLElement | null>(null);
const widgetWidth = computed(() => props.config?.width || 400);
const widgetHeight = computed(() => props.config?.height || 400);
const rowCount = computed(() => props.queueData?.length || 1);
const rowGap = 8;
const headerHeight = ref(50);

onMounted(async () => {
  await nextTick();
  if (headerRowRef.value) {
    headerHeight.value = headerRowRef.value.getBoundingClientRect().height;
  }
});

const isFewRows = computed(() => rowCount.value <= 2);

const availableHeight = computed(() => widgetHeight.value - headerHeight.value);

// const headerFontSize = computed(() => {
//   return Math.max(18, Math.min(40, widgetWidth.value * 0.10, widgetHeight.value * 0.10));
// });

const headerFontSize = computed(() => {
  const w = widgetWidth.value;
  const h = headerHeight.value;
  // Use the smaller of 10% of width or 60% of header height, with reasonable min/max
  return Math.max(18, Math.min(55, Math.min(w * 0.10, h * 0.6)));
});

const rowFontSize = computed(() => {
  const w = widgetWidth.value / 2;
  const h = (availableHeight.value - (rowCount.value - 1) * rowGap) / rowCount.value;
  const maxFont = isFewRows.value ? 70 : 120;
  return Math.max(18, Math.min(maxFont, h * 0.4, w * 0.4));
});

const numberCellStyle = computed(() => ({
  background: props.queueConfig?.numberBgColor || "#2c4a7a",
  color: props.queueConfig?.numberTxtColor || "#fff",
//   padding: "0.2em 0.7em 0.2em 0.2em",
  minWidth: "90px",
  borderRadius: typeof props.borderRadius === "number" ? `${props.borderRadius}px` : "0",
}));

const counterCellStyle = computed(() => ({
  background: props.queueConfig?.counterBgColor || "#ffb300",
  color: props.queueConfig?.counterTxtColor || "#fff",
  padding: "0.2em 0.2em 0.2em 0.7em",
  minWidth: "50px",
  borderRadius: typeof props.borderRadius === "number" ? `${props.borderRadius}px` : "0",
}));

const leafTableStyle = computed(() => ({
  width: "100%",
  height: "100%",
  borderRadius: typeof props.borderRadius === "number" ? `${props.borderRadius}px` : "0",
  overflow: "hidden",
}));

const numberFlex = computed(() => {
  if (props.config?.width <= 400) return 1.3;
  if (props.config?.width < 500) return 1.5;
  return 2;
});
const counterFlex = computed(() => 1);


const language = computed(() => {
  return localStorage.getItem("Language");
});

const headerTextAlign = computed(() => (language.value === 'ar' ? 'center' : 'start'));

</script>

<style scoped>
.leaf-table {
  width: 100%;
  height: 100%;
  display: flex !important;
  flex-direction: column;
  align-items: stretch;
}
.leaf-header-row {
  display: flex !important;
  gap: 40px;
  /* flex: 0 0 8%; */
  min-height: 0;
  /* margin-bottom: 0.8em; */
}
.leaf-header {
  flex: 1;
  font-weight: 400;
  /* text-align: start !important; */
  letter-spacing: 1px;
}
.leaf-rows {
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 0;
  width: 100% !important;
  height: 100% !important;

}
.few-rows {
    display: flex !important;
    justify-content: flex-start !important;
}
.few-rows .leaf-row {
  flex: none !important;
  height: 20% !important;
  margin-bottom: 20px;
}
.leaf-row {
  display: flex !important;
  width: 100%;
  flex: 1 1 0;
  min-height: 0;
  align-items: center;
  gap: 0;
  margin-bottom: 15px;
}
.leaf-row:last-child {
  margin-bottom: 0;
}
.leaf-cell {
  flex: 1 1 0;
  display: flex !important;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  text-transform: uppercase;
  transition: font-size 0.2s;
  border-radius: 0 !important;
}
.leaf-number-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  border-top-left-radius: 0vmin !important;
  border-bottom-left-radius: 7.4vmin !important;
  border-top-right-radius: 7.4vmin !important;
  border-bottom-right-radius: 0vmin !important;
  z-index: 2;
  position: relative;
  height: 100%;
  /* padding: 0.2em 0.7em 0.2em 0.2em; */
  background: var(--number-bg, #000);
  color: var(--number-txt, #fff);
}
.leaf-counter-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  border-top-right-radius: 7.4vmin !important;
  border-bottom-right-radius: 0vmin !important;
  border-top-left-radius: 0 !important;
  border-bottom-left-radius: 0 !important;
  margin-left: -7.4vmin;
  z-index: 1;
  height: 100%;
  /* padding: 0.2em 0.2em 0.2em 0.7em; */
  background: var(--counter-bg, #0000);
  color: var(--counter-txt, #fff);
}
</style>
